import React, { useState, useEffect } from "react"
import { Modal, Radio, Button, Typography, Alert, Collapse } from "antd"
import moment from "moment"
import Strings from "utils/strings"

const { Text } = Typography
const { Panel } = Collapse

export type ExternalDeliveryMode =
	| "split"
	| "external_only"
	| "non_external_only"
	| "all_internal"

interface EligibilityEntry {
	_id: string
	orderId: string
	reason?: { code: string; message: { pt: string; en: string } } | null
	deliveryDate?: string
	assignableToday?: boolean
}

/**
 * How long until the eligible orders can actually be handed to the partner.
 * Eligibility and assignability are different things: routes are routinely built
 * the day before, but the partner handoff is same-day, so a batch can be fully
 * eligible and still a day away from being assignable.
 */
const getAssignableDelay = (eligibleOrders: EligibilityEntry[]) => {
	const pending = eligibleOrders.filter(
		(o) => o.assignableToday === false && o.deliveryDate
	)
	if (pending.length === 0) return null

	const earliest = pending
		.map((o) => moment(o.deliveryDate).startOf("day"))
		.sort((a, b) => a.valueOf() - b.valueOf())[0]

	const days = earliest.diff(moment().startOf("day"), "days")

	return {
		date: earliest.format("DD/MM/YYYY"),
		relative:
			days === 1
				? Strings.orders.externalSplitAssignableTomorrow
				: Strings.orders.externalSplitAssignableInDays.replace(
						"{0}",
						days.toString()
					),
		count: pending.length,
	}
}

interface ExternalDeliverySplitModalProps {
	visible: boolean
	eligibleOrders: EligibilityEntry[]
	ineligibleOrders: EligibilityEntry[]
	totalOrders: number
	submitting: boolean
	onConfirm: (mode: ExternalDeliveryMode) => void
	onCancel: () => void
}

const localized = (message?: { pt: string; en: string } | null) => {
	if (!message) return ""
	return Strings.getLanguage() === "pt" ? message.pt : message.en
}

const ExternalDeliverySplitModal: React.FC<ExternalDeliverySplitModalProps> = ({
	visible,
	eligibleOrders,
	ineligibleOrders,
	totalOrders,
	submitting,
	onConfirm,
	onCancel,
}) => {
	// Split is the only option that sends everything, so it is the safe default.
	const [mode, setMode] = useState<ExternalDeliveryMode>("split")

	useEffect(() => {
		if (visible) setMode("split")
	}, [visible])

	const eligibleCount = eligibleOrders.length
	const nonEligibleCount = totalOrders - eligibleCount
	const assignableDelay = getAssignableDelay(eligibleOrders)

	type Option = { value: ExternalDeliveryMode; label: string; hint: string }

	const options: Option[] = (
		[
			{
				value: "split",
				label: Strings.orders.externalSplitOptionSplit,
				hint: Strings.orders.externalSplitOptionSplitHint
					.replace("{0}", eligibleCount.toString())
					.replace("{1}", nonEligibleCount.toString()),
			},
			{
				value: "external_only",
				label: Strings.orders.externalSplitOptionExternalOnly,
				hint: Strings.orders.externalSplitOptionExternalOnlyHint.replace(
					"{0}",
					eligibleCount.toString()
				),
			},
			{
				value: "non_external_only",
				label: Strings.orders.externalSplitOptionNonExternalOnly,
				hint: Strings.orders.externalSplitOptionNonExternalOnlyHint.replace(
					"{0}",
					nonEligibleCount.toString()
				),
			},
			{
				value: "all_internal",
				label: Strings.orders.externalSplitOptionAllInternal,
				hint: Strings.orders.externalSplitOptionAllInternalHint.replace(
					"{0}",
					totalOrders.toString()
				),
			},
		] as Option[]
	).filter(
		// With nothing left over there is no split to make and no second group to send.
		(option) =>
			nonEligibleCount > 0 ||
			(option.value !== "split" && option.value !== "non_external_only")
	)

	const sendCount =
		mode === "external_only"
			? eligibleCount
			: mode === "non_external_only"
				? nonEligibleCount
				: totalOrders

	return (
		<Modal
			visible={visible}
			title={Strings.orders.externalSplitTitle}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={submitting}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="confirm"
					type="primary"
					loading={submitting}
					onClick={() => onConfirm(mode)}
				>
					{Strings.orders.closerWarningSend.replace(
						"{0}",
						sendCount.toString()
					)}
				</Button>,
			]}
			width={520}
		>
			<div style={{ marginBottom: 16 }}>
				<Text>
					{Strings.orders.externalSplitDescription
						.replace("{0}", eligibleCount.toString())
						.replace("{1}", totalOrders.toString())}
				</Text>
			</div>

			{assignableDelay && (
				<Alert
					type="warning"
					showIcon
					message={Strings.orders.externalSplitFutureTitle
						.replace("{0}", assignableDelay.count.toString())
						.replace("{1}", assignableDelay.relative)}
					description={Strings.orders.externalSplitFutureDescription.replace(
						"{0}",
						assignableDelay.date
					)}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Radio.Group
				value={mode}
				onChange={(e) => setMode(e.target.value)}
				style={{ width: "100%" }}
			>
				{options.map((option) => (
					<Radio
						key={option.value}
						value={option.value}
						style={{
							display: "flex",
							alignItems: "flex-start",
							padding: "10px 12px",
							marginRight: 0,
							marginBottom: 8,
							border: "1px solid",
							borderColor: mode === option.value ? "#1890ff" : "#f0f0f0",
							borderRadius: 6,
							background: mode === option.value ? "#e6f7ff" : undefined,
						}}
					>
						<div style={{ marginLeft: 4 }}>
							<Text strong style={{ fontSize: 13 }}>
								{option.label}
							</Text>
							<div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									{option.hint}
								</Text>
							</div>
						</div>
					</Radio>
				))}
			</Radio.Group>

			{mode !== "split" && mode !== "all_internal" && (
				<Alert
					type="info"
					showIcon
					message={Strings.orders.externalSplitExcludedNotice}
					style={{ marginTop: 4, marginBottom: 12 }}
				/>
			)}

			{ineligibleOrders.length > 0 && (
				<Collapse ghost style={{ marginTop: 4 }}>
					<Panel
						key="ineligible"
						header={Strings.orders.externalSplitIneligibleTitle.replace(
							"{0}",
							ineligibleOrders.length.toString()
						)}
					>
						<div
							style={{
								maxHeight: 220,
								overflowY: "auto",
								border: "1px solid #f0f0f0",
								borderRadius: 6,
							}}
						>
							{ineligibleOrders.map((order, i) => (
								<div
									key={order._id}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 10,
										padding: "8px 12px",
										borderBottom:
											i < ineligibleOrders.length - 1
												? "1px solid #f0f0f0"
												: "none",
									}}
								>
									<Text strong style={{ fontSize: 13 }}>
										{order.orderId || order._id}
									</Text>
									<Text
										type="secondary"
										style={{ fontSize: 12, textAlign: "right" }}
									>
										{localized(order.reason?.message)}
									</Text>
								</div>
							))}
						</div>
					</Panel>
				</Collapse>
			)}
		</Modal>
	)
}

export { ExternalDeliverySplitModal }
